/**
 * A relative popover on desktop or a standard portal modal on mobile
 */

import React, { useRef } from 'react';
import {
  findNodeHandle,
  Platform,
  View,
} from 'react-native';
import { Box } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import Absolute from '../Absolute';
import { useJanitor, useMount } from '../../../hooks';
import { PortalOverlayModalProps } from '../PortalOverlay/PortalOverlayModal';

export type ScreenPosition = {
  /** The absolute y-location of an element in its container */
  top?: number;
  /** The absolute x-location of an element in its container */
  left?: number;
  /** The absolute y-location of an element in its container */
  bottom?: number;
  /** The absolute x-location of an element in its container */
  right?: number;

  zIndex?: number;
};

export type PopoverProps = PortalOverlayModalProps & ScreenPosition & {
  /** Called when the modal is closed */
  onClose: () => void;
  /** A (unique) name for the portal overlay */
  name: string;
  /** Children of the overlay */
  children?: React.ReactNode;
  /** The minimum width of the popover */
  minWidth?: number;
  /** Border radius of desktop popover */
  borderRadius?: number;
  /** Border color of desktop popover */
  borderColor?: string;
};

/**
 * A responsive popover component that acts as a normal PortalOverlay on mobile
 * Or as an absolutely position menu on desktop
 *
 * This component handles closing the popover when a user clicks outside of the menu
 */
export const Popover: React.FCWithChildren<PopoverProps> = ({
  onClose,
  name,
  top,
  left,
  right,
  bottom,
  zIndex,
  minWidth = 0,
  children,
  borderRadius = 8,
  borderColor = BrandColor.LightGrey,
  ...restProps
}) => {
  const janitor = useJanitor();
  const boxRef = useRef<View>();
  const handleFocusOrClick = (event: Event) => {
    if (boxRef.current) {
      /**
       * The intent here is to get the DOM node associated with our box and check for events
       * occurring outside of that node. Running on web, `findNodeHandle` appears to return the
       * element rather than a number as it is typed to do. Since we only run this code on web, the
       * casting here seems acceptable.
       */
      const el: HTMLElement = findNodeHandle(boxRef.current) as unknown as HTMLElement;

      if (el && !el.contains(event.target as Node)) {
        onClose();
        event.preventDefault();
        event.stopPropagation();
      }
    }
  };

  useMount(() => {
    if (Platform.OS === 'web') {
      janitor.addDOMEvent(document.body, 'focus', handleFocusOrClick, true);
      janitor.addDOMEvent(document.body, 'click', handleFocusOrClick, true);
    }
  });

  return (
    <Absolute
      top={top}
      left={left}
      right={right}
      bottom={bottom}
      zIndex={zIndex}
    >
      <Box
        ref={boxRef}
        color={BrandColor.WhiteWash}
        minWidth={minWidth}
        testID={restProps.testID}
        borderRadius={borderRadius}
        borderWidth={1}
        borderColor={borderColor}
        borderStyle="solid"
        showBoxShadow
      >
        {children}
      </Box>
    </Absolute>
  );
};
